"use client"
import React, { useEffect, useState } from 'react'
import { InstrumentAPI } from '@/library/entities/market/data/market'
import { EditorialContentAPI } from '@/library/entities/publications/data/publications'
import { COUNTRY_ID } from '@/data/constants'
import InputDefault from '@/library/blocks/forms/InputDefault'
import Styling from '@/library/styling'
import { FaSearch } from 'react-icons/fa'
import SpinnerPage from '@/library/blocks/spinners/SpinnerPage'
import useDebounce from '@/library/utils/debounce'
import InstrumentListItem from '@/library/entities/market/instrument/components/instrument-list-item/InstrumentListItem'
import ECListItemSmall from '@/library/entities/publications/editorial-content/components/list-item/Small'
import { useRouter, usePathname } from 'next/navigation'


export default function SearchAll({ initialQuery = '', isModal = false, onClose, autoFocus }) {
  const [query, setQuery] = useState(initialQuery)
  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const router = useRouter()
  const pathname = usePathname()

  // Determine if input should be auto-focused.
  // If autoFocus prop is provided, use it. Otherwise, default to true if it is a modal.
  const shouldAutoFocus = autoFocus !== undefined ? autoFocus : isModal

  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery)
      handleOnSearch(initialQuery)
    }
  }, [initialQuery])

  const handleOnSearch = useDebounce(async (value) => {
    if (!value) {
      setItems([])
      setHasSearched(false)
      return
    }

    setIsLoading(true)
    setHasSearched(true)

    // Update URL if not in modal
    if (!isModal) {
      const params = new URLSearchParams(window.location.search)
      if (value) {
        params.set('query', value)
      } else {
        params.delete('query')
      }
      router.replace(`${pathname}?${params.toString()}`)
    }

    try {
      const [instrumentList, editorialContentList] = await Promise.all([
        InstrumentAPI.getList({ country_id: COUNTRY_ID, limit: 10, query: value }),
        EditorialContentAPI.getList({ country_id: COUNTRY_ID, limit: 10, query: value })
      ])

      setItems([
        { kind: 'Instrument', list: instrumentList },
        { kind: 'EditorialContent', list: editorialContentList }
      ])
    } catch (error) {
      console.error("Search failed", error)
    } finally {
      setIsLoading(false)
    }
  }, 500)

  const handleInputChange = (e) => {
    const value = e.target.value
    setQuery(value)
    handleOnSearch(value)
  }

  const renderIsLoading = () => (
    <div className="text-center p-11 rounded-lg text-neutral-500 bg-neutral-50">
      <SpinnerPage />
    </div>
  )

  const renderNotResultFound = () => (
    <div className="col-span-1 md:col-span-2">
      <h2 className={`${Styling.sizes.sm.default} text-center p-11 rounded-lg text-neutral-500 bg-neutral-50`}>
        No results found for "{query}"
      </h2>
    </div>
  )

  return (
    <div className={`w-full ${isModal ? 'h-full flex flex-col' : ''}`}>
      <div className="mb-8">
        <InputDefault
          name="query"
          type="text"
          placeholder="Search for stocks, insights, and more..."
          onChange={handleInputChange}
          formData={{ query }} // InputDefault expects formData object to controlled value
          startContent={<FaSearch className="text-primary-500" />}
          autoFocus={shouldAutoFocus}
          className="w-full"
        />
      </div>

      {isLoading ? (
        renderIsLoading()
      ) : (
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 ${isModal ? 'overflow-y-auto flex-1' : ''}`}>

          {/* Instruments Column */}
          {(items?.[0]?.list?.length > 0) && (
            <div className="instruments">
              <h2 className={`${Styling.sizes.emphasize.default} mb-4`}>Stocks</h2>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {items[0].list.map((item, index) => (
                  <div key={item.identifier || item.pk || item.id || index} onClick={onClose}>
                    <InstrumentListItem item={item} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Editorial Content Column */}
          {(items?.[1]?.list?.length > 0) && (
            <div className="editorial-content">
              <h2 className={`${Styling.sizes.emphasize.default} mb-4`}>Insights</h2>
              <div className="grid grid-cols-1 gap-4">
                {items[1].list.map((item, index) => (
                  <div key={item.identifier || item.pk || item.id || index} onClick={onClose}>
                    <ECListItemSmall item={item} showInstrument={true} roundedImage={true} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Results */}
          {hasSearched &&
            items?.[0]?.list?.length === 0 &&
            items?.[1]?.list?.length === 0 &&
            query && renderNotResultFound()}
        </div>
      )}
    </div>
  )
}